// @ts-nocheck
import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, Download, AlertCircle, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface AdminNcaaCalendarImportProps {
  onImportComplete: () => void;
  onClose: () => void;
}

interface CsvRow {
  title: string;
  start_date: string;
  end_date: string;
  division: string;
  category: string;
  description: string;
}

const AdminNcaaCalendarImport: React.FC<AdminNcaaCalendarImportProps> = ({
  onImportComplete,
  onClose,
}) => {
  const [csvData, setCsvData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState<CsvRow[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const sampleCsv = `title,start_date,end_date,division,category,description
"NCAA Contact Period",2024-06-15,2024-06-30,D1,ncaa_contact,"Summer contact period for Division I"
"NCAA Dead Period",2024-07-01,2024-07-14,D1,ncaa_dead,"No recruiting contact allowed"
"FAFSA Priority Deadline",2024-03-01,,All,fafsa,"Priority deadline for FAFSA submission"
"SAT Test Date",2024-05-04,,All,testing,"SAT testing date"
"National Signing Day",2024-02-07,,All,other,"National Letter of Intent signing day"`;

  const getSourceFromCategory = (category: string): string => {
    if (category.startsWith('ncaa_')) {
      return 'ncaa';
    } else if (category === 'fafsa') {
      return 'fafsa';
    } else if (category === 'testing') {
      return 'testing';
    } else {
      return 'custom';
    }
  };

  const downloadSample = () => {
    const blob = new Blob([sampleCsv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'calendar_events_sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvData(text);
      previewCsv(text);
    };
    reader.readAsText(file);
  };

  const previewCsv = (csvText: string) => {
    try {
      const lines = csvText.trim().split('\n');
      if (lines.length < 2) {
        setErrors(['CSV must contain at least a header row and one data row']);
        return;
      }

      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const expectedHeaders = ['title', 'start_date', 'end_date', 'division', 'category', 'description'];
      
      const missingHeaders = expectedHeaders.filter(h => !headers.includes(h));
      if (missingHeaders.length > 0) {
        setErrors([`Missing required headers: ${missingHeaders.join(', ')}`]);
        return;
      }

      const data: CsvRow[] = [];
      const parseErrors: string[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());
        
        if (values.length !== headers.length) {
          parseErrors.push(`Row ${i + 1}: Expected ${headers.length} columns, got ${values.length}`);
          continue;
        }

        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index];
        });

        // Validate required fields
        if (!row.title) {
          parseErrors.push(`Row ${i + 1}: Title is required`);
        }
        if (!row.start_date) {
          parseErrors.push(`Row ${i + 1}: Start date is required`);
        }
        if (!row.category) {
          parseErrors.push(`Row ${i + 1}: Category is required`);
        }

        // Validate category values
        const validCategories = ['ncaa_contact', 'ncaa_dead', 'ncaa_evaluation', 'ncaa_quiet', 'fafsa', 'testing', 'other'];
        if (row.category && !validCategories.includes(row.category)) {
          parseErrors.push(`Row ${i + 1}: Invalid category '${row.category}'. Must be one of: ${validCategories.join(', ')}`);
        }

        // Validate division values (including "All")
        const validDivisions = ['All', 'D1', 'D2', 'D3', 'NAIA', 'NJCAA', ''];
        if (row.division && !validDivisions.includes(row.division)) {
          parseErrors.push(`Row ${i + 1}: Invalid division '${row.division}'. Must be one of: ${validDivisions.join(', ')}`);
        }

        // Validate date format
        if (row.start_date && !/^\d{4}-\d{2}-\d{2}$/.test(row.start_date)) {
          parseErrors.push(`Row ${i + 1}: Start date must be in YYYY-MM-DD format`);
        }
        if (row.end_date && !/^\d{4}-\d{2}-\d{2}$/.test(row.end_date)) {
          parseErrors.push(`Row ${i + 1}: End date must be in YYYY-MM-DD format`);
        }

        data.push(row as CsvRow);
      }

      setErrors(parseErrors);
      setPreviewData(data);
    } catch (error) {
      setErrors(['Failed to parse CSV: ' + error.message]);
    }
  };

  const handleImport = async () => {
    if (errors.length > 0) {
      toast({
        title: 'Import Failed',
        description: 'Please fix the errors before importing',
        variant: 'destructive'
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      console.log("🏈 Calendar Import: Starting import of", previewData.length, "events");
      
      const eventData = previewData.map(row => {
        const source = getSourceFromCategory(row.category);
        const isCustom = source === 'custom';
        
        return {
          title: row.title,
          start_date: row.start_date,
          end_date: row.end_date || null,
          category: row.category,
          division: row.division === 'All' ? null : row.division || null,
          description: row.description || null,
          source: source,
          is_custom: isCustom,
          profile_id: null,
          sport_id: null,
          related_entity_id: null,
        };
      });

      const { error } = await supabase
        .from('calendar_events')
        .insert(eventData);

      if (error) {
        console.error("🏈 Calendar Import: Insert error:", error);
        throw error;
      }

      console.log("🏈 Calendar Import: Successfully imported", eventData.length, "events");
      
      toast({
        title: 'Import Successful',
        description: `Successfully imported ${eventData.length} calendar events`
      });

      onImportComplete();
      
    } catch (error) {
      console.error('🏈 Calendar Import: Error:', error);
      toast({
        title: 'Import Failed',
        description: `Failed to import events: ${error.message || 'Unknown error'}`,
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col h-full max-h-[80vh]">
      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto space-y-6 pr-2">
        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Import Calendar Events
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-3">
                Upload a CSV file with calendar events. The CSV must include these columns:
              </p>
              <ul className="text-sm text-gray-600 list-disc pl-6 space-y-1">
                <li><strong>title</strong> - Event name (required)</li>
                <li><strong>start_date</strong> - Start date in YYYY-MM-DD format (required)</li>
                <li><strong>end_date</strong> - End date in YYYY-MM-DD format (optional)</li>
                <li><strong>division</strong> - NCAA division (All, D1, D2, D3, NAIA, NJCAA, or empty)</li>
                <li><strong>category</strong> - Event type: ncaa_contact, ncaa_dead, ncaa_evaluation, ncaa_quiet, fafsa, testing, other (required)</li>
                <li><strong>description</strong> - Event description (optional)</li>
              </ul>
            </div>
            
            <Button variant="outline" onClick={downloadSample}>
              <Download className="h-4 w-4 mr-2" />
              Download Sample CSV
            </Button>
          </CardContent>
        </Card>

        {/* File Upload */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="csv-file">Upload CSV File</Label>
            <input
              ref={fileInputRef}
              id="csv-file"
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
            />
          </div>

          <div>
            <Label htmlFor="csv-text">Or Paste CSV Data</Label>
            <Textarea
              id="csv-text"
              value={csvData}
              onChange={(e) => {
                setCsvData(e.target.value);
                previewCsv(e.target.value);
              }}
              placeholder="Paste your CSV data here..."
              rows={8}
            />
          </div>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">Please fix the following errors:</p>
                <ul className="list-disc pl-6 space-y-1">
                  {errors.map((error, index) => (
                    <li key={index} className="text-sm">{error}</li>
                  ))}
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Preview */}
        {previewData.length > 0 && errors.length === 0 && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium">Preview: {previewData.length} events ready to import</p>
              <div className="mt-2 max-h-32 overflow-y-auto">
                <div className="text-xs space-y-1">
                  {previewData.slice(0, 5).map((row, index) => (
                    <div key={index}>
                      {row.title} ({row.start_date} - {row.category})
                    </div>
                  ))}
                  {previewData.length > 5 && (
                    <div className="text-gray-500">...and {previewData.length - 5} more</div>
                  )}
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Fixed Actions at Bottom */}
      <div className="flex-shrink-0 pt-4 border-t bg-white">
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={previewData.length === 0 || errors.length > 0 || isProcessing}
          >
            {isProcessing ? 'Importing...' : `Import ${previewData.length} Events`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminNcaaCalendarImport;